import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Divider,
  Tooltip,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert
} from '@mui/material';
import {
  ArrowBack,
  MenuBook,
  Undo,
  Flag,
  NoteAdd,
  RestartAlt,
  DataObject,
  Close,
  Save
} from '@mui/icons-material';

// Import Configuration and Services
import { SESSION_ID } from './constants/playerConfig';
import {
  getOrCreateLabelingSession,
  loadLabelingSession,
  saveAllLabelingData,
  completeLabelingSession,
  transformTagsFromDB,
  transformPatternsFromDB,
  transformQueryLabelsFromDB,
  transformQueryLabelsForDB
} from '../../../service/labelingService';

// Import telemetry service
import { getTelemetryEventsBySessionAndUser } from '../../workspace/service/telemetryService'; 

// Import the Replay Components for telemetry
import ReplayWorkspace from '../../workspaceReplay2/ReplayWorkspace';
import ReplayControls from '../../workspaceReplay2/components/ReplayControls';
import { usePlayback } from '../../workspaceReplay2/hooks/usePlayback';

// Import Sub-Components
import PlayerSidebar from './components/PlayerSidebar';
import PlayerDialogs from './components/PlayerDialogs';

// Hardcoded session data for replay (fallback)
const HARDCODED_SESSION = {
  id: 643,
  subject: "OOP: Rectangle Class",
  task: "Create a class named Rectangle that takes two parameters in its constructor: width and height. Include a method named get_area that returns the area of the rectangle (width * height).",
  test_code: {
    starter: "# Variable Setup for Unit Tests\nglobal parameter_values\nparameter_values = ['4, 5', '6, 3', '5, 3']\nglobal return_values\nreturn_values = [20, 18, 15]\nglobal result\nresult = []",
    template: "",
    unit_test: "# Execute tests\nfor i, input_expr in enumerate(parameter_values):\n    try:\n        width, height = map(int, input_expr.split(', '))\n        rect = Rectangle(width, height)\n        if rect.get_area() == return_values[i]:\n            result.append(True)\n        else:\n            result.append(False)\n    except:\n        result.append(False)"
  },
  test_list: [
    { name: "Test Case 1", input: "4, 5", expected: "20" },
    { name: "Test Case 2", input: "6, 3", expected: "18" },
    { name: "Test Case 3", input: "5, 3", expected: "15" }
  ]
};

export default function TelemetryPlayerView({ session, currentUser, onBack, onSubmit }) {
  // --- STATE: Data & Player ---
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [telemetryEvents, setTelemetryEvents] = useState([]);

  // --- STATE: Labeling Session ---
  const [labelingSessionId, setLabelingSessionId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // --- STATE: Snackbar ---
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Use hardcoded session data merged with any passed session info
  const sessionData = {
    ...HARDCODED_SESSION,
    ...session,
    task: session?.task || HARDCODED_SESSION.task,
    test_code: session?.test_code || HARDCODED_SESSION.test_code,
    test_list: session?.test_list || HARDCODED_SESSION.test_list
  };

  // --- STATE: Tagging & Labeling ---
  const [taggedBlocks, setTaggedBlocks] = useState([]);
  const [activeRecording, setActiveRecording] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [undoHistory, setUndoHistory] = useState([]);

  // --- STATE: Pattern Selection ---
  const [timelineSelection, setTimelineSelection] = useState(null);
  const [savedPatterns, setSavedPatterns] = useState([]);
  const [queryLabels, setQueryLabels] = useState({});

  // --- STATE: Dialogs ---
  const [quickTagOpen, setQuickTagOpen] = useState(false);
  const [quickTagBehavior, setQuickTagBehavior] = useState(null);
  const [manualTagOpen, setManualTagOpen] = useState(false);
  const [manualTagBehavior, setManualTagBehavior] = useState(null);
  const [manualStartTime, setManualStartTime] = useState('0:00');
  const [manualEndTime, setManualEndTime] = useState('0:00');
  const [codebookOpen, setCodebookOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [rawDataOpen, setRawDataOpen] = useState(false);
  
  // --- STATE: Stop Recording Dialog ---
  const [stopRecordingOpen, setStopRecordingOpen] = useState(false);
  const [stopRecordingEndTime, setStopRecordingEndTime] = useState(0);
  const [stopRecordingConfidence, setStopRecordingConfidence] = useState(null);

  // --- STATE: Form Data ---
  const [sessionNotes, setSessionNotes] = useState('');
  const [isFlagged, setIsFlagged] = useState(false);
  const [confidence, setConfidence] = useState('high');
  const [submitNotes, setSubmitNotes] = useState('');

  // Calculate start time and duration from telemetry events
  const { startTime, duration, filteredEvents } = useMemo(() => {
    if (telemetryEvents.length === 0) {
      return { startTime: 0, duration: 0, filteredEvents: [] }
    }

    const firstEvent = telemetryEvents[0];
    const start = firstEvent.timestamp;

    // Find when all tests passed (optional: stop replay there)
    const allPassedIndex = telemetryEvents.findIndex(e => {
      if (e.type !== 'TEST_CASE_RESULT') return false;
      const { passed_count, total_tests } = e.payload || {};
      return passed_count === total_tests && total_tests > 0;
    });

    const filtered = allPassedIndex !== -1 
      ? telemetryEvents.slice(0, allPassedIndex + 1)
      : telemetryEvents;

    const lastEvent = filtered[filtered.length - 1];
    const end = lastEvent.timestamp;

    return {
      startTime: start,
      duration: end - start,
      filteredEvents: filtered
    };
  }, [telemetryEvents]);

  // Extract chat queries from events
  const chatQueries = useMemo(() => {
    if (!filteredEvents || filteredEvents.length === 0) return [];
    
    const queries = filteredEvents.filter(event => event.type === 'CHAT_QUERY');
    
    return queries.map((event, index) => ({
        index,
        timestamp: event.timestamp,
        time: event.timestamp - startTime,
        text: event.payload?.text || '',
        id: `query-${index}-${event.timestamp}`
      }));
  }, [filteredEvents, startTime]);

  // Initialize playback hook with duration
  const playback = usePlayback(duration);

  // --- EFFECT: Fetch telemetry data ---
  useEffect(() => {
    const loadTelemetryData = async () => {
      try {
        setLoading(true);
        setError(null);

        const vispiSessionId = session?.vispiSessionId || session?.id || SESSION_ID;
        const studentUserId = session?.studentUserId || session?.creater_id;

        console.log('Fetching telemetry for:', { vispiSessionId, studentUserId });

        // Fetch telemetry events
        const events = await getTelemetryEventsBySessionAndUser(vispiSessionId, studentUserId);

        if (!events || events.length === 0) {
          setError('No telemetry events found for this session.');
          return;
        }

        // Sort by timestamp to ensure correct order
        events.sort((a, b) => a.timestamp - b.timestamp);

        setTelemetryEvents(events);

        console.log('Loaded telemetry data:', {
          totalEvents: events.length,
          firstEvent: events[0],
          lastEvent: events[events.length - 1]
        });

      } catch (err) {
        console.error('Error loading telemetry data:', err);
        setError('Failed to load session telemetry: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadTelemetryData();
  }, [session]);

  // --- EFFECT: Initialize labeling session and load existing data ---
  useEffect(() => {
    const initializeLabelingSession = async () => {
      try {
        if (session && currentUser) {
          const labelingSession = await getOrCreateLabelingSession(
            session.vispiSessionId || SESSION_ID,
            session.studentUserId || session.id,
            currentUser,
            session.batchId
          );

          setLabelingSessionId(labelingSession.id);

          if (labelingSession.id) {
            const existingData = await loadLabelingSession(labelingSession.id);
            
            if (existingData) {
              if (existingData.tags && existingData.tags.length > 0) {
                const transformedTags = transformTagsFromDB(existingData.tags);
                setTaggedBlocks(transformedTags);
              }

              if (existingData.patterns && existingData.patterns.length > 0) {
                const transformedPatterns = transformPatternsFromDB(existingData.patterns);
                setSavedPatterns(transformedPatterns);
              }

              // Load query labels
              if (existingData.queryLabels && existingData.queryLabels.length > 0) {
                const transformedQueryLabels = transformQueryLabelsFromDB(existingData.queryLabels);
                setQueryLabels(transformedQueryLabels);
              }

              if (existingData.session) {
                setSessionNotes(existingData.session.session_notes || '');
                setIsFlagged(existingData.session.is_flagged || false);
              }
            }
          }
        }
      } catch (err) {
        console.error('Error initializing labeling session:', err);
      }
    };

    initializeLabelingSession();
  }, [session, currentUser]);

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [taggedBlocks, savedPatterns, queryLabels, sessionNotes, isFlagged]);

  // --- LOGIC: Helpers ---
  const formatTime = (ms) => {
    const num = Number(ms);
    if (!Number.isFinite(num) || num < 0) return '0:00';
    const seconds = Math.floor(num / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDuration = (ms) => {
    const num = Number(ms);
    if (!Number.isFinite(num) || num < 0) return '0:00';
    const seconds = Math.floor(num / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const parseTimeToMs = (timeStr) => {
    const parts = timeStr.split(':');
    if (parts.length === 2) {
      const minutes = parseInt(parts[0], 10) || 0;
      const seconds = parseInt(parts[1], 10) || 0;
      return (minutes * 60 + seconds) * 1000;
    }
    return 0;
  };

  const sessionDuration = duration || 1;

  // --- LOGIC: Save Function ---
  const handleSave = async () => {
    if (!labelingSessionId) {
      setSnackbar({ open: true, message: 'No labeling session to save', severity: 'error' });
      return;
    }

    try {
      setSaving(true);

      const dataToSave = {
        tags: taggedBlocks.map(tag => ({
          id: tag.id,
          dbId: tag.dbId,
          behavior: tag.behavior,
          startTime: tag.startTime,
          endTime: tag.endTime,
          confidence: tag.confidence
        })),
        patterns: savedPatterns.map(pattern => ({
          id: pattern.id,
          dbId: pattern.dbId,
          name: pattern.name,
          description: pattern.description,
          startTime: pattern.startTime,
          endTime: pattern.endTime,
          color: pattern.color,
          isPreQuery: pattern.isPreQuery || false,
          behaviors: pattern.behaviors?.map(b => ({
            id: b.id,
            dbId: b.dbId,
            overlapStart: b.overlapStart,
            overlapEnd: b.overlapEnd
          })) || [],
          assessment: pattern.assessment
        })),
        queryLabels: transformQueryLabelsForDB(queryLabels, chatQueries),
        sessionNotes,
        isFlagged
      };

      const result = await saveAllLabelingData(labelingSessionId, dataToSave);

      if (result.tags) {
        const updatedTags = transformTagsFromDB(result.tags);
        setTaggedBlocks(updatedTags);
      }

      if (result.patterns) {
        const updatedPatterns = transformPatternsFromDB(result.patterns);
        setSavedPatterns(updatedPatterns);
      }

      // Update query labels from response
      if (result.queryLabels) {
        const updatedQueryLabels = transformQueryLabelsFromDB(result.queryLabels);
        setQueryLabels(updatedQueryLabels);
      }

      setHasUnsavedChanges(false);
      setSnackbar({ open: true, message: 'Progress saved successfully!', severity: 'success' });
    } catch (err) {
      console.error('Error saving:', err);
      setSnackbar({ open: true, message: 'Failed to save progress', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // --- LOGIC: Tagging Actions ---
  const handleStartRecording = (behavior) => {
    setActiveRecording({ behavior, startTime: playback.currentTime });
  };

  const handleStopRecording = () => {
    if (!activeRecording) return;
    setStopRecordingEndTime(playback.currentTime);
    setStopRecordingConfidence(null);
    setStopRecordingOpen(true);
  };

  const handleStopRecordingConfirm = () => {
    if (!activeRecording || !stopRecordingConfidence) return;
    const newBlock = {
      id: Date.now(),
      behavior: activeRecording.behavior,
      startTime: activeRecording.startTime,
      endTime: stopRecordingEndTime,
      confidence: stopRecordingConfidence
    };
    setTaggedBlocks(prev => {
      setUndoHistory(history => [...history, prev]);
      return [...prev, newBlock].sort((a, b) => a.startTime - b.startTime);
    });
    setActiveRecording(null);
    setStopRecordingOpen(false);
    setStopRecordingConfidence(null);
  };

  const handleStopRecordingCancel = () => {
    setStopRecordingOpen(false);
    setStopRecordingConfidence(null);
  };

  const handleQuickTagOpen = (behavior) => {
    setQuickTagBehavior(behavior);
    setQuickTagOpen(true);
  };

  const handleQuickTagApply = (secondsBack) => {
    if (!quickTagBehavior) return;
    const endTime = playback.currentTime;
    const startTime = Math.max(0, playback.currentTime - (secondsBack * 1000));
    
    const newBlock = {
      id: Date.now(),
      behavior: quickTagBehavior,
      startTime,
      endTime
    };
    
    setTaggedBlocks(prev => {
      setUndoHistory(history => [...history, prev]);
      return [...prev, newBlock].sort((a, b) => a.startTime - b.startTime);
    });
    setQuickTagOpen(false);
    setQuickTagBehavior(null);
  };

  const handleManualTagOpen = (behavior) => {
    setManualTagBehavior(behavior);
    setManualStartTime(formatTime(playback.currentTime));
    setManualEndTime(formatTime(playback.currentTime));
    setManualTagOpen(true);
  };

  const handleManualTagApply = () => {
    if (!manualTagBehavior) return;
    const startTime = parseTimeToMs(manualStartTime);
    const endTime = parseTimeToMs(manualEndTime);
    
    if (endTime <= startTime) { alert('End time must be after start time'); return; }
    if (endTime > sessionDuration) { alert('End time cannot exceed session duration'); return; }
    
    const newBlock = {
      id: Date.now(),
      behavior: manualTagBehavior,
      startTime,
      endTime
    };
    
    setTaggedBlocks(prev => {
      setUndoHistory(history => [...history, prev]);
      return [...prev, newBlock].sort((a, b) => a.startTime - b.startTime);
    });
    setManualTagOpen(false);
    setManualTagBehavior(null);
  };

  const handleDeleteBlock = (blockId) => {
    setTaggedBlocks(prev => {
      setUndoHistory(history => [...history, prev]);
      return prev.filter(b => b.id !== blockId);
    });
  };

  const handleBlockClick = (block) => {
    playback.seek(block.startTime);
  };

  const handleUndo = () => {
    if (undoHistory.length === 0) return;
    const previousState = undoHistory[undoHistory.length - 1];
    setTaggedBlocks(previousState);
    setUndoHistory(prev => prev.slice(0, -1));
  };

  const handleReset = () => {
    if (taggedBlocks.length === 0 && savedPatterns.length === 0) return;
    setUndoHistory(prev => [...prev, taggedBlocks]);
    setTaggedBlocks([]);
    setSavedPatterns([]);
    setQueryLabels({});
    setTimelineSelection(null);
    setSessionNotes('');
    setIsFlagged(false);
  };

  // --- LOGIC: Pattern Actions ---
  const handleSavePattern = (pattern, isUpdate = false) => {
    if (isUpdate) {
      setSavedPatterns(prev => 
        prev.map(p => p.id === pattern.id ? pattern : p)
          .sort((a, b) => a.startTime - b.startTime)
      );
    } else {
      setSavedPatterns(prev => [...prev, pattern].sort((a, b) => a.startTime - b.startTime));
    }
  };

  const handleDeletePattern = (patternId) => {
    setSavedPatterns(prev => prev.filter(p => p.id !== patternId));
  };

  const handleQueryLabelChange = (queryId, label) => {
    setQueryLabels(prev => ({ ...prev, [queryId]: label }));
  };

  const handleSubmitConfirm = async () => {
    try {
      setSaving(true);

      await handleSave();

      const submitData = {
        confidence,
        submitNotes,
        sessionNotes,
        isFlagged
      };

      await completeLabelingSession(labelingSessionId, submitData);

      const submissionData = {
        tags: taggedBlocks,
        patterns: savedPatterns,
        queryLabels,
        notes: sessionNotes,
        submitNotes,
        confidence,
        isFlagged,
        sessionId: sessionData?.id,
        labelingSessionId,
        timestamp: new Date().toISOString(),
        summary: {
          totalPatterns: savedPatterns.length,
          assessedPatterns: savedPatterns.filter(p => p.assessment?.isComplete).length,
          avgProductivity: savedPatterns.filter(p => p.assessment?.productivity).reduce((sum, p) => sum + p.assessment.productivity, 0) / savedPatterns.filter(p => p.assessment?.productivity).length || null,
          hasPreQueryPattern: savedPatterns.some(p => p.isPreQuery),
          labeledQueries: Object.keys(queryLabels).length,
          totalQueries: chatQueries.length
        }
      };

      setSubmitModalOpen(false);
      setSnackbar({ open: true, message: 'Review submitted successfully!', severity: 'success' });
      
      onSubmit(submissionData);
    } catch (err) {
      console.error('Error submitting:', err);
      setSnackbar({ open: true, message: 'Failed to submit review', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // --- RENDER ---
  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#111827' }}>
      
      {/* 1. Header Bar */}
      <Box sx={{ p: 2, bgcolor: '#111827', borderBottom: '1px solid #374151', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={onBack} sx={{ color: '#9ca3af', '&:hover': { color: '#ffffff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ color: '#ffffff', fontWeight: 600 }}>
                Session {sessionData?.id}
              </Typography>
              {isFlagged && (
                <Chip icon={<Flag sx={{ fontSize: 14 }} />} label="Flagged" size="small" sx={{ bgcolor: '#92400e', color: '#fcd34d', fontWeight: 500, height: 20 }} />
              )}
              {hasUnsavedChanges && (
                <Chip label="Unsaved" size="small" sx={{ bgcolor: '#374151', color: '#9ca3af', fontWeight: 500, height: 20 }} />
              )}
            </Box>
            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
              {session?.student} • {formatDuration(sessionDuration)} • {filteredEvents.length} events
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="View Raw Data">
            <IconButton onClick={() => setRawDataOpen(true)} sx={{ color: '#e5e7eb', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              <DataObject />
            </IconButton>
          </Tooltip>
          
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, bgcolor: '#374151' }} />
          
          <Tooltip title="Codebook">
            <IconButton onClick={() => setCodebookOpen(true)} sx={{ color: '#e5e7eb', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              <MenuBook />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Undo">
            <span>
              <IconButton onClick={handleUndo} disabled={undoHistory.length === 0} sx={{ color: undoHistory.length === 0 ? '#4b5563' : '#e5e7eb', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                <Undo />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={isFlagged ? 'Remove flag' : 'Flag for review'}>
            <IconButton onClick={() => setIsFlagged(!isFlagged)} sx={{ color: isFlagged ? '#fcd34d' : '#e5e7eb', bgcolor: isFlagged ? 'rgba(251, 191, 36, 0.1)' : 'transparent', '&:hover': { bgcolor: isFlagged ? 'rgba(251, 191, 36, 0.2)' : 'rgba(255,255,255,0.1)' } }}>
              <Flag />
            </IconButton>
          </Tooltip>

          <Tooltip title="Session notes">
            <IconButton onClick={() => setNotesOpen(true)} sx={{ color: sessionNotes ? '#60a5fa' : '#e5e7eb', bgcolor: sessionNotes ? 'rgba(96, 165, 250, 0.1)' : 'transparent', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              <NoteAdd />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1, bgcolor: '#374151' }} />

          <Tooltip title="Reset all tags">
            <span>
              <IconButton onClick={handleReset} disabled={taggedBlocks.length === 0 && savedPatterns.length === 0} sx={{ color: (taggedBlocks.length === 0 && savedPatterns.length === 0) ? '#4b5563' : '#f87171', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' } }}>
                <RestartAlt />
              </IconButton>
            </span>
          </Tooltip>

          <Button 
            variant="outlined" 
            startIcon={saving ? <CircularProgress size={16} sx={{ color: '#60a5fa' }} /> : <Save />}
            onClick={handleSave}
            disabled={saving || !labelingSessionId}
            sx={{ 
              borderColor: '#3b82f6', 
              color: '#60a5fa',
              '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.1)', borderColor: '#60a5fa' }, 
              textTransform: 'none', 
              fontWeight: 600, 
              borderRadius: 2, 
              px: 2
            }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>

          <Button 
            variant="contained" 
            onClick={() => setSubmitModalOpen(true)} 
            sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }, textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3 }}
          >
            Submit Review
          </Button>
        </Box>
      </Box>

      <Grid container sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        
        {/* 2. Replay Player Section */}
        <Grid item xs={9} sx={{ bgcolor: '#1f2937', display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: '#9ca3af' }}>
              <Box>
                <CircularProgress sx={{ color: '#3b82f6' }} />
                <Typography variant="body2" sx={{ mt: 2 }}>Loading telemetry data...</Typography>
              </Box>
            </Box>
          ) : error ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: '#9ca3af' }}>
              <Box>
                <Typography variant="body1" sx={{ mb: 2, color: '#f87171' }}>{error}</Typography>
                <Typography variant="body2">Check that the session has telemetry data.</Typography>
              </Box>
            </Box>
          ) : filteredEvents.length === 0 ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: '#9ca3af' }}>
              <Box>
                <Typography variant="body1" sx={{ mb: 2 }}>No telemetry events found for this session.</Typography>
                <Typography variant="body2">The session may not have any recorded activity.</Typography>
              </Box>
            </Box>
          ) : (
            <>
              {/* Telemetry Replay Workspace */}
              <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                <div style={{
                  width: '133.33%',
                  height: '133.33%',
                  transform: 'scale(0.75)',
                  transformOrigin: 'top left',
                }}>
                  <ReplayWorkspace 
                    playback={playback} 
                    events={filteredEvents}
                    startTime={startTime}
                    task={sessionData.task}
                    starterCode=""
                  />
                </div>
              </Box>

              {/* Replay Controls */}
              <ReplayControls
                currentTime={playback.currentTime}
                duration={playback.duration}
                isPlaying={playback.isPlaying}
                playbackSpeed={playback.playbackSpeed}
                onTogglePlay={playback.togglePlay}
                onSeek={playback.seek}
                onReset={playback.reset}
                onCycleSpeed={playback.cycleSpeed}
                taggedBlocks={taggedBlocks}
                showLabeling={true}
                activeRecording={activeRecording}
                events={filteredEvents}
                startTime={startTime}
              />
            </>
          )}
        </Grid>

        {/* 3. Sidebar Section */}
        <Grid item xs={3} sx={{ height: '100%', overflow: 'hidden' }}>
          <PlayerSidebar 
            activeTab={activeTab} setActiveTab={setActiveTab}
            activeRecording={activeRecording} taggedBlocks={taggedBlocks}
            handleStartRecording={handleStartRecording} handleStopRecording={handleStopRecording}
            handleQuickTagOpen={handleQuickTagOpen} handleManualTagOpen={handleManualTagOpen}
            handleDeleteBlock={handleDeleteBlock} handleBlockClick={handleBlockClick}
            formatTime={formatTime}
            timelineSelection={timelineSelection}
            setTimelineSelection={setTimelineSelection}
            savedPatterns={savedPatterns}
            onSavePattern={handleSavePattern}
            onDeletePattern={handleDeletePattern}
            chatQueries={chatQueries}
            onSeekToTime={(time) => playback.seek(time)}
            queryLabels={queryLabels}
            onUpdateQueryLabel={handleQueryLabelChange}
          />
        </Grid>
      </Grid>

      {/* 4. Modals and Dialogs */}
      <PlayerDialogs 
        quickTagOpen={quickTagOpen} setQuickTagOpen={setQuickTagOpen}
        quickTagBehavior={quickTagBehavior} handleQuickTagApply={handleQuickTagApply}
        
        manualTagOpen={manualTagOpen} setManualTagOpen={setManualTagOpen}
        manualTagBehavior={manualTagBehavior}
        manualStartTime={manualStartTime} setManualStartTime={setManualStartTime}
        manualEndTime={manualEndTime} setManualEndTime={setManualEndTime}
        handleManualTagApply={handleManualTagApply}
        
        currentTime={playback.currentTime} formatTime={formatTime} 
        formatDuration={formatDuration} sessionDuration={sessionDuration}
        
        codebookOpen={codebookOpen} setCodebookOpen={setCodebookOpen}
        notesOpen={notesOpen} setNotesOpen={setNotesOpen}
        sessionNotes={sessionNotes} setSessionNotes={setSessionNotes}
        
        submitModalOpen={submitModalOpen} setSubmitModalOpen={setSubmitModalOpen}
        handleSubmitConfirm={handleSubmitConfirm}
        taggedBlocks={taggedBlocks} isFlagged={isFlagged}
        confidence={confidence} setConfidence={setConfidence}
        submitNotes={submitNotes} setSubmitNotes={setSubmitNotes}
        savedPatterns={savedPatterns}
        saving={saving}
        
        stopRecordingOpen={stopRecordingOpen}
        activeRecording={activeRecording}
        stopRecordingEndTime={stopRecordingEndTime}
        stopRecordingConfidence={stopRecordingConfidence}
        setStopRecordingConfidence={setStopRecordingConfidence}
        handleStopRecordingConfirm={handleStopRecordingConfirm}
        handleStopRecordingCancel={handleStopRecordingCancel}
      />

      {/* Raw Data Viewer Dialog */}
      <Dialog 
        open={rawDataOpen} 
        onClose={() => setRawDataOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { bgcolor: '#1f2937', color: '#e5e7eb', maxHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #374151',
          bgcolor: '#111827'
        }}>
          <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
            Telemetry Raw Data
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const dataStr = JSON.stringify({
                  sessionData,
                  telemetryEvents: filteredEvents,
                  taggedBlocks,
                  savedPatterns,
                  queryLabels,
                  sessionNotes,
                  isFlagged,
                  labelingSessionId,
                  metadata: {
                    startTime,
                    duration,
                    totalEvents: filteredEvents.length,
                    totalTags: taggedBlocks.length,
                    totalQueryLabels: Object.keys(queryLabels).length
                  }
                }, null, 2);
                navigator.clipboard.writeText(dataStr);
                setSnackbar({ open: true, message: 'Copied to clipboard!', severity: 'success' });
              }}
              sx={{ 
                color: '#60a5fa', 
                borderColor: '#60a5fa',
                textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(96, 165, 250, 0.1)', borderColor: '#60a5fa' }
              }}
            >
              Copy JSON
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const dataStr = JSON.stringify({
                  sessionData,
                  telemetryEvents: filteredEvents,
                  taggedBlocks,
                  savedPatterns,
                  queryLabels,
                  sessionNotes,
                  isFlagged,
                  labelingSessionId,
                  metadata: {
                    startTime,
                    duration,
                    totalEvents: filteredEvents.length,
                    totalTags: taggedBlocks.length,
                    totalQueryLabels: Object.keys(queryLabels).length
                  }
                }, null, 2);
                const blob = new Blob([dataStr], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `telemetry_session_${sessionData?.id}_data.json`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              sx={{ 
                color: '#10b981', 
                borderColor: '#10b981',
                textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.1)', borderColor: '#10b981' }
              }}
            >
              Download JSON
            </Button>
            <IconButton 
              onClick={() => setRawDataOpen(false)}
              sx={{ color: '#9ca3af', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              bgcolor: '#0f172a',
              color: '#e2e8f0',
              fontSize: 12,
              fontFamily: 'monospace',
              overflowX: 'auto',
              overflowY: 'auto',
              maxHeight: 'calc(80vh - 120px)',
            }}
          >
            {JSON.stringify({
              sessionData,
              telemetryEvents: filteredEvents.slice(0, 50), // Show first 50 for performance
              totalEvents: filteredEvents.length,
              taggedBlocks,
              savedPatterns,
              queryLabels,
              sessionNotes,
              isFlagged,
              labelingSessionId,
              metadata: {
                startTime,
                duration,
                totalEvents: filteredEvents.length,
                totalTags: taggedBlocks.length,
                totalPatterns: savedPatterns.length,
                totalQueryLabels: Object.keys(queryLabels).length
              }
            }, null, 2)}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </Box>
  );
}